'use client';

import React from 'react';
import { Money } from './Money';

export interface ServiceRowProps {
  title: string;
  duration: string; // e.g. "30 דק׳"
  price: number; // ₪-leading, bidi-isolated, end-aligned
  thumb?: string | null;
  selectable?: boolean;
  selected?: boolean;
  onToggle?: () => void;
  divider?: boolean;
  style?: React.CSSProperties;
}

export function ServiceRow({
  title,
  duration,
  price,
  thumb = null,
  selectable = false,
  selected = false,
  onToggle,
  divider = true,
  style = {},
}: ServiceRowProps) {
  return (
    <div
      onClick={selectable ? onToggle : undefined}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--sp-4)',
        minHeight: 64,
        paddingBlock: 'var(--sp-4)',
        paddingInline: 'var(--sp-3)',
        marginBlock: selected ? 2 : 0,
        borderBottom: divider && !selected ? '1px solid var(--border-subtle)' : 'none',
        // Selected = an indigo OUTLINE, not a filled wash — the same
        // border-only convention the app's own service cards use (a
        // selected card still reads as "ink content on white").
        border: selected ? '1.5px solid var(--accent)' : '1.5px solid transparent',
        background: 'transparent',
        borderRadius: selected ? 'var(--r-md)' : 0,
        boxSizing: 'border-box',
        cursor: selectable ? 'pointer' : 'default',
        transition: 'border-color var(--dur-micro)',
        fontFamily: 'var(--font-sans)',
        ...style,
      }}
    >
      {thumb && (
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: 'var(--r-sm)',
            flex: '0 0 auto',
            backgroundImage: `url(${thumb})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 'var(--fs-body-l)',
            fontWeight: 'var(--fw-medium)' as React.CSSProperties['fontWeight'],
            color: 'var(--text-primary)',
          }}
        >
          {title}
        </div>
        <div
          style={{ fontSize: 'var(--fs-caption)', color: 'var(--text-secondary)', marginTop: 2 }}
        >
          {duration}
        </div>
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-4)',
          flex: '0 0 auto',
        }}
      >
        <span
          style={{
            fontSize: 'var(--fs-label)',
            fontWeight: 'var(--fw-semibold)' as React.CSSProperties['fontWeight'],
            color: 'var(--text-primary)',
          }}
        >
          <Money amount={price} />
        </span>
        {selectable && (
          <span
            style={{
              width: 26,
              height: 26,
              borderRadius: 'var(--r-full)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: selected ? 'none' : '1.5px solid var(--border-strong)',
              background: selected ? 'var(--brand)' : 'transparent',
              color: 'var(--on-brand)',
              fontSize: 15,
              fontWeight: 700,
              lineHeight: 1,
              transition: 'background var(--dur-micro)',
            }}
          >
            {selected ? '✓' : '+'}
          </span>
        )}
      </div>
    </div>
  );
}
